import { useCustomMutation } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { DEFINE_FIELDS, FILL_FIELDS } from './model';

/**
 * E37 · the practice's diary-card commands from the portal — DefineDiaryCard (this practice's card), FillDiaryCardInSession
 * and PublishDiaryCardTemplate (the backoffice) — each ONE deliberate act (a button), posted with
 * `errorNotification: false` so the screen reports the outcome itself (portal conventions): a CODED refusal
 * (`checkIn.errors.DIARYCARD_*`, already in the therapist's language) is shown verbatim, against the thing it refused;
 * anything else is the screen's own copy plus Sentry.
 */
export type DiaryCardOperation =
  | 'defineDiaryCard'
  | 'fillDiaryCardInSession'
  | 'publishDiaryCardTemplate';

const INPUT_TYPE: Record<DiaryCardOperation, string> = {
  defineDiaryCard: 'DefineDiaryCardInput!',
  fillDiaryCardInSession: 'FillDiaryCardInSessionInput!',
  publishDiaryCardTemplate: 'PublishDiaryCardTemplateInput!',
};

const FIELDS: Record<DiaryCardOperation, string[]> = {
  defineDiaryCard: DEFINE_FIELDS,
  fillDiaryCardInSession: FILL_FIELDS,
  publishDiaryCardTemplate: DEFINE_FIELDS,
};

export type DiaryCardCommandResult =
  | { ok: true; recorded: boolean; cardId?: string; cards?: number }
  | { ok: false; code: string | undefined; message: string };

export const useDiaryCardCommand = () => {
  const { mutateAsync, isLoading } = useCustomMutation<{
    cardId?: string;
    cards?: number;
    recorded: boolean;
  }>();

  const run = useCallback(
    async (
      operation: DiaryCardOperation,
      input: Record<string, unknown>,
      fallback: string,
    ): Promise<DiaryCardCommandResult> => {
      try {
        const res = await mutateAsync({
          url: '',
          method: 'post',
          values: {},
          errorNotification: false,
          successNotification: false,
          meta: {
            operation,
            fields: FIELDS[operation],
            variables: {
              input: { name: 'input', type: INPUT_TYPE[operation], value: input },
            },
          },
        });
        const data = res?.data;
        if (!data) return { ok: false, code: undefined, message: fallback };
        return {
          ok: true,
          recorded: !!data.recorded,
          cardId: data.cardId,
          cards: data.cards,
        };
      } catch (error) {
        const info = graphqlErrorInfo(error);
        if (info.code && info.message) {
          return { ok: false, code: info.code, message: info.message };
        }
        Sentry.captureException(error);
        return { ok: false, code: info.code, message: fallback };
      }
    },
    [mutateAsync],
  );

  return { run, isLoading };
};
